import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';
import { CarouselProps } from './Carousel.types';
import {
  CarouselWrapper,
  ImageWrapper,
  NavigationButton,
  NavigationWrapper,
} from './Carousel.css';
import { RiArrowLeftLine, RiArrowRightLine } from '@remixicon/react';

import 'swiper/swiper-bundle.css';
import { useIntl } from 'react-intl';
import { trackClickEvent } from '@frontend/tracking';
import ImagesLightbox from '../ImagesLightbox/ImagesLightbox';
import { useMediaQuery, useTheme } from '@mui/material';

export default function Carousel({
  id,
  images,
  width,
  height,
  caption,
}: CarouselProps) {
  const intl = useIntl();
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
  const [lightboxActive, setLightboxActive] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  const swiperRef = useRef<SwiperInstance | null>(null);

  const enableKeyboard = () => {
    if (swiperRef.current?.keyboard) {
      swiperRef.current.keyboard.enable();
    }
  };

  const disableKeyboard = () => {
    if (swiperRef.current?.keyboard) {
      swiperRef.current.keyboard.disable();
    }
  };

  if (images.length === 0) {
    return null;
  }

  const handleNavigationClick = () => {
    trackClickEvent({
      elementId: `${id}.carousel.navigation.clicked`,
    });
  };

  const handleSlideClick = () => {
    trackClickEvent({
      elementId: `${id}.carousel.slide.clicked`,
    });
    setLightboxActive(true);
  };

  const goToSlide = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
    }
  };

  return (
    <>
      <CarouselWrapper
        height={height}
        width={width}
        tabIndex={0}
        onMouseEnter={enableKeyboard}
        onMouseLeave={disableKeyboard}
        onFocus={enableKeyboard}
        onBlur={disableKeyboard}
      >
        <Swiper
          slidesPerView={1}
          modules={[Navigation, Pagination, Keyboard]}
          navigation={{
            nextEl: '.next-slide',
            prevEl: '.prev-slide',
          }}
          allowTouchMove={isMobile}
          pagination={{
            type: 'fraction',
          }}
          keyboard={{
            enabled: false,
          }}
          lazyPreloadPrevNext={1}
          loop
          onClick={handleSlideClick}
          onSlideChange={swiper => {
            setActiveSlideIndex(swiper.realIndex);
          }}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
        >
          {images.map((url, index) => (
            <SwiperSlide key={index}>
              <ImageWrapper>
                <img
                  src={url}
                  alt={caption ?? `Carousel slide ${index + 1}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </ImageWrapper>
            </SwiperSlide>
          ))}
          <NavigationWrapper>
            <NavigationButton
              aria-label={intl.formatMessage({
                id: 'ui-elements.carousel.button.prev-slide',
              })}
              className="prev-slide"
              id="ui-elements.carousel.button.prev-slide"
              onClick={handleNavigationClick}
            >
              <RiArrowLeftLine size={12} />
            </NavigationButton>
            <NavigationButton
              aria-label={intl.formatMessage({
                id: 'ui-elements.carousel.button.next-slide',
              })}
              className="next-slide"
              id="ui-elements.carousel.button.next-slide"
              onClick={handleNavigationClick}
            >
              <RiArrowRightLine size={12} />
            </NavigationButton>
          </NavigationWrapper>
        </Swiper>
      </CarouselWrapper>
      <ImagesLightbox
        slidesList={images}
        isOpen={lightboxActive}
        closeLightbox={index => {
          setLightboxActive(false);
          goToSlide(index);
        }}
        imageIndex={activeSlideIndex}
      />
    </>
  );
}
